'use client';

import { useState } from 'react';

import type { ApiInternshipFunction } from '#/api/types/ApiInternshipFunction';
import type { ApiJobFunction } from '#/api/types/ApiJobFunction';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type { ApiSynonymMini } from '#/api/types/ApiSynonymMini';
import type { ApiOrgId } from '#/api/types/ApiTypedId';
import type { ApiVolopFunction } from '#/api/types/ApiVolopFunction';
import { Button } from '#/components/Button/Button';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useCopyToClipboard } from '#/hooks/useCopyToClipboard';
import { useI18n } from '#/i18n/hooks/useI18n';
import {
  listingRichTextTruncateAtAbout2Lines,
  ListingRichTextTruncated,
} from '#/modules/listing/components/RichText/Truncated/ListingRichTextTruncated';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { spacing } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  applyUrl: string | null;
  applyEmail: string | null;
  applyText: string | null;
  applyTextTruncated: string | null;
  isPreview: boolean;
  listingName: string;
  listingType: ApiListingApplicationListingType;
  listingId: string;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  synonym: ApiSynonymMini | null;
  functions: ApiJobFunction[] | ApiInternshipFunction[] | ApiVolopFunction[];
  applied: boolean;
  onMarkAsApplied: () => Promise<void>;
};

export function ListingViewApplyExternal({
  applyEmail,
  applyText,
  applyTextTruncated,
  applyUrl,
  functions,
  isPreview,
  listingName,
  listingType,
  listingId,
  orgType,
  orgId,
  synonym,
  applied,
  onMarkAsApplied,
}: Props) {
  const { i18n } = useI18n();

  const { copyToClipboard } = useCopyToClipboard({
    copiedMessageTimeoutMs: 'default',
  });

  const [copied, setCopied] = useState(false);

  const { user } = useUser();

  const sendEmailButton = (
    <Button
      type="link"
      variant={applyUrl ? 'secondary' : 'primary'}
      qaId="apply-button"
      href={`mailto:${applyEmail}`}
      target="_blank"
      size="large"
      icon={{ name: 'mail' }}
      onClick={() => {
        trackClicked('Apply To Listing', {
          applied_via: 'email',
          listing_type: listingType,
          listing_id: listingId,
          org_id: orgId ?? null,
          org_type: orgType ?? null,
          has_ats: false,
        });

        if (user && !applied && listingType === 'JOB') {
          showModal('MARK_APPLIED', { onMarkAsApplied });
        } else if (!user) {
          showModal('EMAIL_ALERT', {
            listingFunctions: functions,
            orgId: orgId ?? undefined,
            listingName,
            listingType,
            synonym,
          });
        }
      }}
    >
      {i18n('Send Email')}
    </Button>
  );

  return (
    <>
      {(applyUrl ?? applyEmail) && (
        <div style={{ display: 'flex', gap: spacing[16], flexWrap: 'wrap' }}>
          {applyUrl && (
            <Button
              type="link"
              anchorType="native"
              variant="primary"
              qaId="apply-button"
              href={applyUrl}
              icon={{ name: 'arrow-up-right', position: 'right' }}
              target="_blank"
              size="large"
              onClick={(event) => {
                trackAnchorClicked(event, 'Apply To Listing', {
                  applied_via: 'external link',
                  listing_type: listingType,
                  listing_id: listingId,
                  org_id: orgId ?? null,
                  org_type: orgType ?? null,
                  has_ats: false,
                });

                if (user && !applied && listingType === 'JOB') {
                  showModal('MARK_APPLIED', { onMarkAsApplied });
                } else {
                  showModal('EMAIL_ALERT', {
                    listingFunctions: functions,
                    orgId: orgId ?? undefined,
                    listingName,
                    listingType,
                    synonym,
                  });
                }
              }}
            >
              {i18n('Go to Website')}
            </Button>
          )}

          {/*
            NOTE: we're intentionally conditionally rendering the tooltip as
            relying on triggerCondition={isPreview} broke the app behaviour. More context here:
            https://app.shortcut.com/idealisttechproduct/story/64548/master-apply-email-button-not-working-as-expected
            Once we fix the tooltip we can pass isPreview to triggerCondition.
          */}
          {applyEmail && isPreview ? (
            <Tooltip
              content={i18n("You can't take this action in Preview Mode")}
              triggerCondition={true}
              variant="filled"
              placement="bottom-center"
              size="large"
            >
              <div
                style={
                  isPreview
                    ? { pointerEvents: 'none', cursor: 'not-allowed' }
                    : undefined
                }
              >
                {sendEmailButton}
              </div>
            </Tooltip>
          ) : null}

          {applyEmail && !isPreview ? sendEmailButton : null}

          {applyEmail && (
            <Button
              type="button"
              variant="secondary"
              icon={{ name: 'copy' }}
              size="large"
              onClick={() => {
                trackClicked('Apply To Listing', {
                  applied_via: 'copied email',
                  listing_type: listingType,
                  listing_id: listingId,
                  org_id: orgId ?? null,
                  org_type: orgType ?? null,
                  has_ats: false,
                });

                void copyToClipboard(applyEmail).then(() => {
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 5000);
                  return;
                });
              }}
              data-url={applyEmail}
            >
              {copied ? i18n('Copied') : i18n('Copy Email')}
            </Button>
          )}
        </div>
      )}

      {applyText && (
        <div style={{ marginTop: 30 }}>
          <ListingRichTextTruncated
            showMoreLinkTitle={i18n('Read more about how to apply')}
            showLessLinkTitle={i18n('Read less about how to apply')}
            html={applyText}
            truncateAtHeight={
              applyTextTruncated && applyTextTruncated !== applyText
                ? listingRichTextTruncateAtAbout2Lines
                : null
            }
            eventName="Apply To Listing"
            eventProperties={{
              applied_via: 'external link',
              listing_type: listingType,
              listing_id: listingId,
              org_id: orgId ?? null,
              org_type: orgType ?? null,
              has_ats: false,
            }}
          />
        </div>
      )}
    </>
  );
}
